import { Tab, Tabs } from '@mui/material';
import { useMemo } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import HeaderTitle from '../../components/HeaderTitle/HeaderTitle';
import Loader from '../../components/Loader/Loader';
import { useSurveySession, useSurveySessions } from '../../hooks/useSurveySessions';
import { VoiceSurveyDetail, VoiceSurveyHero } from './VoiceSurveyDetail';
import styles from './VoiceSurveys.module.scss';
import { formatDateTime } from './surveySessionFormat';

const MAX_TABS = 100;
const OPEN_CALL_MAX_AGE_MS = 60 * 60 * 1000;

export const VoiceSurveyDetailPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const { session, loading } = useSurveySession(id);
  const accountId = session?.account?.id;
  const { data: accountSessions } = useSurveySessions({
    accountId: accountId ? String(accountId) : '',
    limit: String(MAX_TABS),
    skip: !accountId,
  });

  // The API returns the account's calls newest first, which is the tab order.
  const tabs = useMemo(
    () =>
      (accountSessions || []).map((accountSession) => ({
        name: String(accountSession.id),
        label: formatDateTime(accountSession.created_at),
      })),
    [accountSessions]
  );
  const selectedTab = tabs.findIndex((tab) => tab.name === String(id));
  // A call open longer than an hour was dropped without an end; the API ignores it too.
  const live = (accountSessions || []).some(
    (accountSession) =>
      !accountSession.ended_at && Date.now() - new Date(accountSession.created_at) < OPEN_CALL_MAX_AGE_MS
  );
  const account = session?.account;

  return (
    <>
      <HeaderTitle title={'Voice Survey'} />
      <div className='page-layout'>
        <div className={`header bordered ${styles.profileHeader}`}>
          <div className='header-title'>
            <Link to='/voice-surveys'>&larr; Voice Surveys</Link>
            {/* The page is the person; each call is a tab. */}
            <h1>{account?.name || account?.phone || `Call #${id}`}</h1>
            {account?.name && account.phone && <p className={styles.subtitle}>{account.phone}</p>}
          </div>
        </div>
        {session && <VoiceSurveyHero session={session} live={live} />}
        {/* Underlined tabs inside the content width: they switch between this
            person's calls, they are not page navigation. */}
        {selectedTab >= 0 && (
          <h2 className={styles.callsHeading}>Calls ({tabs.length})</h2>
        )}
        {selectedTab >= 0 && (
          <Tabs
            className={styles.callTabs}
            value={selectedTab}
            onChange={(_event, index) => history.push(`/voice-surveys/${tabs[index].name}`)}
            variant='scrollable'
            scrollButtons='auto'
          >
            {tabs.map((tab) => (
              <Tab key={tab.name} label={tab.label} />
            ))}
          </Tabs>
        )}
        <Loader isLoading={loading}>{session && <VoiceSurveyDetail session={session} />}</Loader>
      </div>
    </>
  );
};
